import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AuthError } from 'auth/utils.ts';
import { useTranslation } from 'react-i18next';

import { uploadProductsManagementFile } from '@/activityLog/productsManagement/Inventory/api/productManagementApi.ts';
import { productsManagementQueries } from '@/activityLog/productsManagement/queries/productsManagementQueries.ts';

export type Error = {
  title: string;
  description: string[];
};

export const useProductsManagementUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const queryClient = useQueryClient();

  const [importError, setImportError] = useState<Error | null>(null);

  const { t } = useTranslation();

  const isMutating = useIsMutating({ mutationKey: productsManagementQueries.upload });
  const { mutate: uploadFileMutation } = useMutation({
    mutationFn: (file: File) => {
      return uploadProductsManagementFile(file);
    },
    mutationKey: productsManagementQueries.upload,
    onSuccess: () => {
      setFile(null);
      queryClient.invalidateQueries({ queryKey: productsManagementQueries.all });
    },
    onError: (error) => {
      if (error instanceof AuthError) {
        throw error;
      } else {
        try {
          const parsedError = JSON.parse(error.message);
          if (parsedError?.validationErrors) {
            const messages: string[] = [];
            for (const key in parsedError.validationErrors) {
              if (parsedError.validationErrors[key][0]) {
                messages.push(parsedError.validationErrors[key][0]?.message);
              }
            }
            if (messages.length > 0) {
              return setImportError({
                title: t('activityLog.productsManagement.uploadFailed'),
                description: messages,
              });
            }
          }

          setImportError({
            title: t('activityLog.productsManagement.uploadFailed'),
            description: [t('common.somethingWrong')],
          });
        } catch {
          setImportError({
            title: t('activityLog.productsManagement.uploadFailed'),
            description: [t('common.somethingWrong')],
          });
        }
      }
    },
  });

  const uploadFile = () => {
    if (file) {
      uploadFileMutation(file);
    }
  };

  return {
    selectedFile: file,
    selectFile: setFile,
    uploadFile,
    importError,
    setImportError,
    isPending: !!isMutating,
  };
};
